import { useState, useEffect } from 'react';
import { fetchBatchesForItem, BatchStockRecord } from '@/api/batch.api';

interface Props {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  currentBatchNumber?: string;
  currentLabelQty: number;
  onApply: (batchNumber: string, labelQty: number, expiryDate?: string, mfgDate?: string) => void;
}

export default function BatchSelectModal({
  open,
  onClose,
  itemId,
  itemName,
  currentBatchNumber,
  currentLabelQty,
  onApply,
}: Props) {
  const [batches, setBatches] = useState<BatchStockRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<string>(currentBatchNumber || '');
  const [labelQty, setLabelQty] = useState<number>(currentLabelQty || 1);
  const [customMode, setCustomMode] = useState(false);
  const [customBatchNo, setCustomBatchNo] = useState('');
  const [customExpiry, setCustomExpiry] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedBatch(currentBatchNumber || '');
    setLabelQty(currentLabelQty || 1);
    setCustomMode(false);
    setCustomBatchNo('');
    setCustomExpiry('');

    async function loadBatches() {
      try {
        setLoading(true);
        const data = await fetchBatchesForItem(itemId);
        setBatches(data);
        if (!currentBatchNumber && data.length > 0) {
          setSelectedBatch(data[0].batchNumber);
        }
      } catch (err) {
        console.error('Failed to load batches:', err);
      } finally {
        setLoading(false);
      }
    }

    loadBatches();
  }, [open, itemId, currentBatchNumber, currentLabelQty]);

  const handleConfirm = () => {
    if (customMode) {
      if (!customBatchNo.trim()) return;
      onApply(customBatchNo.trim(), labelQty, customExpiry || undefined);
    } else {
      if (!selectedBatch) return;
      const bObj = batches.find((b) => b.batchNumber === selectedBatch);
      onApply(
        selectedBatch,
        labelQty,
        bObj?.expiryDate ? bObj.expiryDate.split('T')[0] : undefined,
        bObj?.mfgDate ? bObj.mfgDate.split('T')[0] : undefined
      );
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
              <i className="ri-archive-line text-lg" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">Select Batch Number</h3>
              <p className="text-xs text-slate-500 font-medium truncate max-w-xs">{itemName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              {customMode ? 'Custom Batch Entry' : 'Available Batches'}
            </span>
            <button
              onClick={() => setCustomMode(!customMode)}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer"
            >
              {customMode ? 'Select from List' : '+ Enter Custom Batch'}
            </button>
          </div>

          {customMode ? (
            <div className="space-y-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Batch Number *
                </label>
                <input
                  type="text"
                  placeholder="e.g. BATCH-2024-001"
                  value={customBatchNo}
                  onChange={(e) => setCustomBatchNo(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-indigo-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Expiry Date (Optional)
                </label>
                <input
                  type="date"
                  value={customExpiry}
                  onChange={(e) => setCustomExpiry(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-indigo-500 bg-white"
                />
              </div>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <i className="ri-loader-4-line text-2xl animate-spin mb-2 text-emerald-600" />
              <p className="text-xs">Loading batches...</p>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <i className="ri-archive-line text-3xl mb-2 opacity-50 block" />
              <p className="text-sm font-medium text-slate-600">No batches found</p>
              <p className="text-xs text-slate-400 mt-1">
                No active batches recorded in inventory for this item.
              </p>
              <button
                onClick={() => setCustomMode(true)}
                className="mt-3 px-3 py-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium border border-emerald-200 rounded-lg inline-flex items-center gap-1"
              >
                <i className="ri-add-line" /> Enter Batch Manually
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {batches.map((b) => {
                const isSelected = selectedBatch === b.batchNumber;
                return (
                  <label
                    key={b.id || b.batchNumber}
                    className={`flex items-start justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/60'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <input
                        type="radio"
                        name="batch-radio"
                        checked={isSelected}
                        onChange={() => setSelectedBatch(b.batchNumber)}
                        className="w-4 h-4 mt-0.5 accent-emerald-600 cursor-pointer shrink-0"
                      />
                      <div>
                        <div className="font-mono text-xs font-bold text-slate-800">
                          {b.batchNumber}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                          {b.expiryDate && (
                            <span>Exp: {new Date(b.expiryDate).toLocaleDateString()}</span>
                          )}
                          {b.warehouseName && <span>WH: {b.warehouseName}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                        Qty: {b.currentQty}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* Label Quantity Field */}
          <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
            <div>
              <label className="block text-xs font-semibold text-slate-700">
                Number of Labels to Print
              </label>
              <p className="text-[11px] text-slate-400">
                Copies of this batch label to be generated
              </p>
            </div>
            <input
              type="number"
              min={1}
              value={labelQty}
              onChange={(e) => setLabelQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-20 text-center border border-slate-200 rounded-md py-1.5 text-xs font-bold outline-none focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={customMode ? !customBatchNo.trim() : !selectedBatch}
            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors disabled:opacity-40 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-check-line" />
            Apply Batch
          </button>
        </div>
      </div>
    </div>
  );
}
